const CMS_API_BASE = (process.env.NEXT_PUBLIC_CMS_API_URL || "http://localhost:3001").replace(/\/$/, "");
// Public Supabase Storage host for property photos. The API already returns absolute
// image URLs, so this is only a fallback for any relative "property-images/..." path.
// Defaults to the production storage host (also allow-listed in next.config.ts), so no
// env var is needed; override with NEXT_PUBLIC_SUPABASE_URL only for a different bucket.
const SUPABASE_BASE = (process.env.NEXT_PUBLIC_SUPABASE_URL || "https://zrtqlkcwvmlmqcrdxdry.supabase.co").replace(/\/$/, "");

const ZOHO_IMAGE_MARKER_PREFIX = "zoho-path:";
const ZOHO_CREATOR_HOST_PATTERN = /(?:^|\.)creator(?:app)?\.zoho\.(?:com|in|eu|com\.au)$/i;

const PROPERTY_IMAGE_FALLBACK = "/images/cover-image.webp";

type ResolveImageOptions = {
  propertyId?: string | null;
  imageIndex?: number | null;
  adminId?: string | null;
};

type ImageLikeProperty = {
  id?: string | null;
  cover_image?: unknown;
  images?: unknown;
};

type ParsedZohoMarker = {
  adminId: string | null;
  path: string;
};

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) return [];

    if (normalized.startsWith("[") && normalized.endsWith("]")) {
      try {
        const parsed = JSON.parse(normalized);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (typeof item === "string" ? item.trim() : ""))
            .filter(Boolean);
        }
      } catch {
        // Ignore parse errors and fall back to comma split.
      }
    }

    return normalized
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function parseZohoImageMarker(value: string): ParsedZohoMarker | null {
  const normalized = (value || "").trim();
  if (!normalized.startsWith(ZOHO_IMAGE_MARKER_PREFIX)) {
    return null;
  }

  const payload = normalized.slice(ZOHO_IMAGE_MARKER_PREFIX.length);
  if (!payload) return null;

  const separatorIndex = payload.indexOf(":");
  if (separatorIndex === -1) {
    return {
      adminId: null,
      path: safeDecodeURIComponent(payload),
    };
  }

  const adminPart = payload.slice(0, separatorIndex);
  const pathPart = payload.slice(separatorIndex + 1);

  return {
    adminId: adminPart ? safeDecodeURIComponent(adminPart) : null,
    path: pathPart ? safeDecodeURIComponent(pathPart) : "",
  };
}

function isAbsoluteHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function isZohoCreatorUrl(value: string): boolean {
  if (!isAbsoluteHttpUrl(value)) return false;

  try {
    const parsed = new URL(value);
    return ZOHO_CREATOR_HOST_PATTERN.test(parsed.hostname);
  } catch {
    return false;
  }
}

function isZohoReference(value: string): boolean {
  const normalized = (value || "").trim();
  if (!normalized) return false;

  if (parseZohoImageMarker(normalized)) return true;
  if (isZohoCreatorUrl(normalized)) return true;
  if (normalized.startsWith("/api/v2/")) return true;
  if (normalized.startsWith("/sharedBy/")) return true;

  return false;
}

export function resolvePropertyImageUrl(rawUrl: string | null | undefined, options?: ResolveImageOptions): string {
  const normalized = (rawUrl || "").trim();
  if (!normalized) return PROPERTY_IMAGE_FALLBACK;

  if (normalized.startsWith("/api/zoho-image?")) {
    return `${CMS_API_BASE}${normalized}`;
  }

  if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
    if (normalized.includes("/api/zoho-image?")) {
      return normalized;
    }
  }

  const marker = parseZohoImageMarker(normalized);
  let adminId = options?.adminId || null;
  let path = normalized;

  if (marker) {
    adminId = marker.adminId || adminId;
    path = marker.path;
  }

  if (isZohoReference(path)) {
    const params = new URLSearchParams({ path });

    const propertyId = options?.propertyId?.trim();
    if (propertyId) {
      params.set("propertyId", propertyId);
    }

    if (typeof options?.imageIndex === "number" && Number.isFinite(options.imageIndex)) {
      params.set("idx", String(options.imageIndex));
    }

    if (adminId) {
      params.set("adminId", adminId);
    }

    return `${CMS_API_BASE}/api/zoho-image?${params.toString()}`;
  }

  if (/\/storage\/v1\/object\/public\//i.test(normalized)) {
    return normalized;
  }

  if (SUPABASE_BASE && normalized.startsWith("property-images/")) {
    return `${SUPABASE_BASE}/storage/v1/object/public/${normalized}`;
  }

  if (SUPABASE_BASE && (normalized.startsWith("images/") || normalized.startsWith("videos/"))) {
    return `${SUPABASE_BASE}/storage/v1/object/public/property-images/${normalized}`;
  }

  return normalized;
}

export function getPrimaryPropertyImageUrl(property: ImageLikeProperty): string | null {
  const propertyId = typeof property.id === "string" ? property.id : null;

  const coverImages = toStringArray(property.cover_image);
  if (coverImages.length > 0) {
    return resolvePropertyImageUrl(coverImages[0], {
      propertyId,
      imageIndex: 0,
    });
  }

  const images = toStringArray(property.images);
  if (images.length > 0) {
    return resolvePropertyImageUrl(images[0], {
      propertyId,
      imageIndex: 0,
    });
  }

  return null;
}

export function shouldUnoptimizedPropertyImage(src: string): boolean {
  const normalized = (src || "").toLowerCase();
  if (!normalized) return false;

  if (normalized.includes("/api/zoho-image?")) return true;
  if (normalized.includes("supabase.co/storage/")) return true;

  return false;
}
